import { useEexCapability } from "../use-eex-capability";
import {
  EexLiveUniverseResponseSchema,
  EexMarketDataFeedHealthResponseSchema,
  EexMarketDataInstrumentsResponseSchema,
  EexMarketDataPublicTradesResponseSchema,
  EexMarketDataScopesResponseSchema,
  EexMarketDataSettlementMarksResponseSchema,
  EexMarketDataTopOfBookResponseSchema,
  EexStoragePreferenceUpdateRequestSchema,
  EexSubjectPreferenceMutationResponseSchema,
  EexWatchlistUpdateRequestSchema,
  type EexMarketDataFeedHealthItem as FeedHealth,
  type EexMarketDataFeedHealthResponse as HealthResponse,
  type EexMarketDataInstrument as MatrixInstrument,
  type EexMarketDataPublicTrade as PublicTrade,
  type EexMarketDataSettlementMark as SettlementMark,
  type EexMarketDataScopesResponse as ScopeResponse,
  type EexMarketDataTopOfBookItem as TopOfBook,
} from "@chro/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  Database,
  History,
  LayoutGrid,
  LineChart,
  List,
  Radio,
  Search,
  Settings2,
  Star,
  X,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { api, apiContract, ApiError, type ApiResponseSchema } from "../../../lib/api";
import {
  eexCachedPath,
  eexCompactDecimal,
  eexCustomMatrixDeliveryPeriodKeys,
  eexDefaultMatrixConfiguration,
  eexDefaultMatrixDeliveryPeriodKeys,
  eexFilterForView,
  eexHealthHeadline,
  eexMarketTimestampTitle,
  eexMarketSearchMatches,
  eexMarketSpread,
  eexMarketWatchlistMatches,
  eexObservationAgeLabel,
  eexPublicEventWindow,
  buildEexMarketMatrix,
  type EexMatrixBoardConfiguration,
  type EexMatrixDeliveryPeriod,
  type EexMatrixProductGroup,
  type EexLiveMarketFilter,
  type EexMarketWatchlistFilter,
} from "../../../lib/eex-live-market";
import { cn } from "../../../lib/utils";
import { ForwardCurveBoard } from "./ForwardCurveBoard";

type ApiFamily = "derivatives" | "gas_spot" | "index";
type MarketFamily = Exclude<ApiFamily, "index">;
type Page<T> = { asOf: string; items: T[]; page: { limit: number; offset: number; nextOffset: number | null } };
type CachedCollection<T> = { asOf: string; items: T[]; truncated?: boolean };
type IndexPublication = {
  revisionKey: string;
  commodityCode: string;
  areaCode: string;
  indexCode: string;
  displayName: string | null;
  tradingDate: string;
  deliveryDay: string;
  price: string;
  volume: string | null;
  currency: string | null;
  uom: string | null;
  receivedAt: string;
};
type View = "market" | "tape" | "index" | "health";
type MarketBoardMode = "matrix" | "curve" | "catalogue";

const EMPTY_FILTER: EexLiveMarketFilter = {
  apiFamily: "",
  marketSegment: "",
  commodityCode: "",
  areaCode: "",
  dataset: "",
};

const LIVE_MARKET_CATALOGUE_RETRY_COUNT = 2;

function shouldRetryLiveMarketCatalogue(failureCount: number, error: unknown): boolean {
  if (failureCount >= LIVE_MARKET_CATALOGUE_RETRY_COUNT) return false;
  if (!(error instanceof ApiError)) return true;
  return error.status === 408 || error.status === 429 || error.status >= 500;
}

export function LiveMarketPanel({
  canAdminister = false,
  mobileReadOnly = false,
}: {
  canAdminister?: boolean;
  mobileReadOnly?: boolean;
}) {
  const [view, setView] = useState<View>("market");
  const [filter, setFilter] = useState<EexLiveMarketFilter>(EMPTY_FILTER);
  const effectiveFilter = useMemo(() => eexFilterForView(filter, view), [filter, view]);
  const scopesQ = useQuery({
    queryKey: ["eex-market-data", "cached-scopes"],
    queryFn: () => apiContract(
      EexMarketDataScopesResponseSchema,
      "/eex/market-data/reference/scopes"
    ),
    retry: false,
    staleTime: 30_000,
  });
  const healthQ = useQuery({
    queryKey: ["eex-market-data", "feed-health", effectiveFilter],
    queryFn: () => apiContract(
      EexMarketDataFeedHealthResponseSchema,
      eexCachedPath("feed-health", effectiveFilter)
    ),
    enabled: scopesQ.isSuccess,
    retry: false,
    refetchInterval: 5_000,
  });

  if (scopesQ.isLoading) {
    return <section className="rounded-lg border border-border p-4 text-sm text-muted">Loading cached EEX scopes…</section>;
  }
  if (scopesQ.isError) {
    const unavailable = scopesQ.error instanceof ApiError && scopesQ.error.status === 503;
    return (
      <section className="rounded-lg border border-border p-4 text-sm text-muted">
        {unavailable
          ? "Live Market is not enabled for this company. Existing manual quotes and trade-entry workflows remain available."
          : `Unable to load cached EEX market data: ${(scopesQ.error as Error).message}`}
      </section>
    );
  }

  const scopes = scopesQ.data;
  if (!scopes) {
    return <section className="rounded-lg border border-border p-4 text-sm text-muted">Cached EEX scope metadata is unavailable.</section>;
  }
  const tapeHistoryAvailable = scopes.scopes.some((scope) =>
    scope.dataset === "public_trades"
      && (scope.rolloutPhase === "history_enabled" || scope.rolloutPhase === "quote_enabled")
      && matchesScopeFilter(scope, eexFilterForView(filter, "tape"))
  );
  const storageScopeIds = new Set(scopes.scopes.filter((scope) =>
    scope.dataset === "top_of_book"
      && scope.rolloutGatePresent
      && !scope.companySelectionViewOnly
      && scope.rolloutPhase !== "shadow"
      && matchesScopeFilter(scope, eexFilterForView(filter, "market"))
  ).map((scope) => scope.id));
  const historyScopeIds = new Set(scopes.scopes.filter((scope) =>
    scope.dataset === "top_of_book"
      && (
        scope.snapshotPriority != null
        || scope.rolloutPhase === "history_enabled"
        || scope.rolloutPhase === "quote_enabled"
      )
      && matchesScopeFilter(scope, eexFilterForView(filter, "market"))
  ).map((scope) => scope.id));
  const headline = eexHealthHeadline(healthQ.data?.items.map((item) => item.displayState) ?? []);
  return (
    <section
      aria-label="Live EEX market"
      className={cn(
        "min-w-0 space-y-3 rounded-lg border border-border",
        mobileReadOnly ? "p-3" : "p-4"
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold"><Radio size={16} /> Live Market</div>
          <p className="mt-1 text-xs text-muted">
            Cached EEX best bid/ask, public market activity and published indices. Public tape rows are not this company&apos;s private trades.
          </p>
        </div>
        <HealthBadge state={headline} />
      </div>

      <div className={cn(mobileReadOnly ? "grid grid-cols-2 gap-2" : "flex flex-wrap gap-2")}>
        <FilterSelect mobile={mobileReadOnly} label="Market" value={filter.apiFamily} values={scopes.filters.apiFamilies} onChange={(apiFamily) => setFilter({ ...filter, apiFamily })} />
        {!mobileReadOnly && <FilterSelect label="Product type" value={filter.marketSegment} values={scopes.filters.marketSegments} onChange={(marketSegment) => setFilter({ ...filter, marketSegment })} />}
        <FilterSelect mobile={mobileReadOnly} label="Commodity" value={filter.commodityCode} values={scopes.filters.commodityCodes} onChange={(commodityCode) => setFilter({ ...filter, commodityCode })} />
        <FilterSelect mobile={mobileReadOnly} label="Area / hub" value={filter.areaCode} values={scopes.filters.areaCodes} onChange={(areaCode) => setFilter({ ...filter, areaCode })} />
        {!mobileReadOnly && view === "health" ? (
          <FilterSelect label="Dataset" value={filter.dataset} values={scopes.filters.datasets} onChange={(dataset) => setFilter({ ...filter, dataset })} />
        ) : (
          <span className={cn(
            "inline-flex items-center rounded border border-border px-2 text-xs text-muted",
            mobileReadOnly ? "col-span-2 min-h-11" : "h-7"
          )}>
            Showing: {effectiveFilter.dataset === "top_of_book" ? "best bid / ask" : effectiveFilter.dataset.replaceAll("_", " ")}
          </span>
        )}
      </div>

      {mobileReadOnly ? (
        <div className="flex min-h-11 items-center border-b border-border text-sm font-medium text-accent">
          <LineChart className="mr-2 h-4 w-4" aria-hidden="true" /> Best bid / ask
        </div>
      ) : (
        <div className="flex flex-wrap gap-1 border-b border-border">
          <Tab active={view === "market"} onClick={() => setView("market")} icon={<LineChart size={13} />} label="Best bid / ask" />
          <Tab active={view === "tape"} onClick={() => setView("tape")} icon={<History size={13} />} label="Public tape" />
          <Tab active={view === "index"} onClick={() => setView("index")} icon={<Database size={13} />} label="Indices" />
          <Tab active={view === "health"} onClick={() => setView("health")} icon={<Activity size={13} />} label="Feed health" />
        </div>
      )}

      <div className="rounded border border-accent/30 bg-accent/5 px-3 py-2 text-xs text-fg">
        <span className="font-medium text-accent">Live viewing is available.</span>{" "}
        Background validation and feed monitoring continue while you use this workspace; they do not replace or
        interrupt manual quotes, trade entry or reconciliation.
      </div>

      <FeedWarnings items={healthQ.data?.items ?? []} />
      {mobileReadOnly ? (
        <MobileTopOfBookBoard
          filter={effectiveFilter}
          healthItems={healthQ.data?.items ?? []}
        />
      ) : view === "market" && (
        <TopOfBookTable
          filter={effectiveFilter}
          canAdminister={canAdminister}
          storageScopeIds={storageScopeIds}
          historyScopeIds={historyScopeIds}
        />
      )}
      {view === "tape" && <PublicTapeTable filter={effectiveFilter} historyAvailable={tapeHistoryAvailable} />}
      {view === "index" && <IndexTable filter={effectiveFilter} />}
      {view === "health" && <HealthTable query={healthQ} />}
    </section>
  );
}

function useTopOfBookQueries(filter: EexLiveMarketFilter) {
  const query = useQuery({
    queryKey: ["eex-market-data", "top-of-book", filter],
    queryFn: ({ signal }) => fetchCachedPages(
      EexMarketDataTopOfBookResponseSchema,
      "top-of-book",
      filter,
      20_000,
      { companyUniverseSelected: "true" },
      signal
    ),
    retry: false,
    refetchInterval: 5_000,
  });
  const instrumentsQ = useQuery({
    queryKey: ["eex-market-data", "matrix-instruments", filter],
    queryFn: ({ signal }) => fetchCachedPages(
      EexMarketDataInstrumentsResponseSchema,
      "reference/instruments",
      filter,
      20_000,
      { matrixEligible: "true", companyUniverseSelected: "true" },
      signal
    ),
    retry: shouldRetryLiveMarketCatalogue,
    retryDelay: 500,
    staleTime: 60_000,
  });
  const settlementsQ = useQuery({
    queryKey: ["eex-market-data", "settlement-marks", filter],
    queryFn: ({ signal }) => fetchCachedPages(
      EexMarketDataSettlementMarksResponseSchema,
      "settlement-marks",
      { ...filter, dataset: "" },
      20_000,
      { companyUniverseSelected: "true" },
      signal
    ),
    retry: false,
    refetchInterval: 60_000,
  });

  return { query, instrumentsQ, settlementsQ };
}

function CatalogueLoadError({
  message,
  onRetry,
  retrying,
}: {
  message: string;
  onRetry: () => void;
  retrying: boolean;
}) {
  return (
    <div className="rounded border border-negRed/30 px-3 py-6 text-center text-xs text-negRed">
      <div>{message}</div>
      <button
        type="button"
        disabled={retrying}
        onClick={onRetry}
        className="mt-3 rounded border border-negRed/30 px-2 py-1 text-xs font-medium hover:border-negRed disabled:cursor-not-allowed disabled:opacity-60"
      >
        {retrying ? "Retrying catalogue…" : "Retry catalogue"}
      </button>
    </div>
  );
}

function MobileTopOfBookBoard({
  filter,
  healthItems,
}: {
  filter: EexLiveMarketFilter;
  healthItems: FeedHealth[];
}) {
  const { query, instrumentsQ, settlementsQ } = useTopOfBookQueries(filter);

  if (query.isLoading) {
    return <div className="py-6 text-center text-xs text-muted">Loading cached live instruments…</div>;
  }
  if (query.isError) {
    return (
      <div className="py-6 text-center text-xs text-negRed">
        {query.error?.message ?? "Cached data request failed"}
      </div>
    );
  }
  if (instrumentsQ.isLoading) {
    return <div className="py-8 text-center text-xs text-muted">Loading the EEX delivery-period catalogue…</div>;
  }
  if (instrumentsQ.isError) {
    return (
      <CatalogueLoadError
        message="The board catalogue could not be loaded. Cached prices remain available."
        retrying={instrumentsQ.isFetching}
        onRetry={() => void instrumentsQ.refetch()}
      />
    );
  }

  return (
    <div className="min-w-0 space-y-3">
      <MobileMarketMatrixBoard
        instruments={instrumentsQ.data?.items ?? []}
        quotes={query.data?.items ?? []}
        settlements={settlementsQ.data?.items ?? []}
        healthItems={healthItems}
      />
      {settlementsQ.isError && (
        <div className="rounded border border-amber-500/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          Official settlement marks could not be refreshed. Live bid/ask and Last remain available.
        </div>
      )}
    </div>
  );
}

function MobileMarketMatrixBoard({
  instruments,
  quotes,
  settlements,
  healthItems,
}: {
  instruments: MatrixInstrument[];
  quotes: TopOfBook[];
  settlements: SettlementMark[];
  healthItems: FeedHealth[];
}) {
  const { matrix, active, defaults, restoreDefaults, missingGroupCount, storageError } = useResolvedMarketMatrix(instruments, quotes, settlements);
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(() => new Set());
  const [expandedRows, setExpandedRows] = useState<ReadonlySet<string>>(() => new Set());

  if (matrix.groups.length === 0 || matrix.periods.length === 0) {
    return (
      <div className="rounded border border-border px-3 py-8 text-center text-xs text-muted">
        No outright futures or gas contracts are currently available for the selected market filters.
      </div>
    );
  }

  const groupByKey = new Map(matrix.groups.map((group) => [group.key, group]));
  const periodKeys = new Set(active.deliveryPeriodKeys);
  const selectedGroups = active.productGroupKeys.flatMap((key) => {
    const group = groupByKey.get(key);
    return group ? [group] : [];
  });
  const selectedPeriods = matrix.periods.filter((period) => periodKeys.has(period.key));

  const toggleGroup = (key: string) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleRow = (key: string) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="min-w-0 space-y-3" aria-label="Mobile EEX best bid and ask board">
      <MarketBoardRecovery
        missingGroupCount={missingGroupCount}
        omittedDefaultCount={defaults.productGroupKeys.filter((key) => !active.productGroupKeys.includes(key)).length}
        storageError={storageError}
        onRestore={restoreDefaults}
      />
      {(selectedGroups.length === 0 || selectedPeriods.length === 0) && (
        <p className="text-sm text-muted">No product groups or delivery periods are configured for this board. Restore market defaults to show the available markets.</p>
      )}
      <div className="flex flex-wrap gap-x-3 text-xs text-muted">
        <span>{selectedGroups.length} product groups</span>
        <span>{selectedPeriods.length} delivery periods</span>
        <span>Prices update in place</span>
      </div>

      {selectedGroups.map((group, groupIndex) => {
        const rows = selectedPeriods.flatMap((period) => {
          const cell = group.cells.get(period.key);
          return cell ? [{ period, cell }] : [];
        });
        const visibleQuotes = rows.flatMap(({ cell }) => cell.quote ? [cell.quote] : []);
        const scopeIds = new Set(rows.map(({ cell }) => cell.instrument.scopeId));
        const feedStates = summarizeValues(
          healthItems
            .filter((item) => scopeIds.has(item.scopeId))
            .map((item) => item.displayState)
        );
        const quoteStates = summarizeQuoteStates(visibleQuotes);
        const marketTimestamp = latestTimestamp(visibleQuotes.map((quote) => quote.marketTimestamp));
        const receivedAt = latestTimestamp(visibleQuotes.map((quote) => quote.receivedAt));
        const collapsed = collapsedGroups.has(group.key);
        const headingId = `eex-mobile-product-${groupIndex}`;
        const tableId = `eex-mobile-product-table-${groupIndex}`;

        return (
          <section
            key={group.key}
            aria-labelledby={headingId}
            className="min-w-0 overflow-hidden rounded-lg border border-border bg-bg"
          >
            <div className="border-b border-border bg-border/[0.06] px-3 py-2">
              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h2 id={headingId} className="break-words text-sm font-semibold uppercase tracking-wide">
                    {group.label}
                  </h2>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                    <span>{group.familyCode ?? group.commodityCode}</span>
                    <span aria-hidden="true">·</span>
                    <span>{group.currency ?? "—"}/{group.uom ?? "—"}</span>
                  </div>
                  <div
                    className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted"
                    aria-label={`Price updates: ${feedStates.length > 0 ? feedStates.map(({ value, count }) => `${plainFeedState(value)}${count > 1 ? ` (${count})` : ""}`).join(", ") : "unavailable"}`}
                  >
                    <span className="font-medium text-fg">Price updates:</span>
                    {feedStates.length > 0 ? feedStates.map(({ value, count }) => (
                      <span key={value} className="inline-flex items-center gap-1">
                        <StateBadge value={value} label={plainFeedState(value)} />
                        {count > 1 && <span aria-label={`${count} markets`}>×{count}</span>}
                      </span>
                    )) : <span>unavailable</span>}
                  </div>
                  <div
                    className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted"
                    aria-label={`Prices: ${quoteStates.length > 0 ? quoteStates.map(({ freshnessState, coverageState, count }) => `${plainQuoteState(freshnessState, coverageState)}${count > 1 ? ` (${count})` : ""}`).join(", ") : "none saved"}`}
                  >
                    <span className="font-medium text-fg">Prices:</span>
                    {quoteStates.length > 0 ? quoteStates.map(({ freshnessState, coverageState, count }) => (
                      <span key={`${freshnessState}:${coverageState}`} className="inline-flex items-center gap-1">
                        <StateBadge
                          value={freshnessState}
                          label={plainQuoteState(freshnessState, coverageState)}
                        />
                        {count > 1 && <span aria-label={`${count} contracts`}>×{count}</span>}
                      </span>
                    )) : <span>none saved</span>}
                  </div>
                </div>
                <button
                  type="button"
                  aria-expanded={!collapsed}
                  aria-controls={tableId}
                  aria-label={`${collapsed ? "Expand" : "Collapse"} ${group.label}`}
                  onClick={() => toggleGroup(group.key)}
                  className="min-h-11 shrink-0 touch-manipulation rounded border border-border px-3 text-xs font-medium text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  {collapsed ? "Show" : "Hide"}
                </button>
              </div>
              <div className="mt-1 break-words text-xs text-muted">
                {marketTimestamp ? `EEX price time ${formatUtcTimestamp(marketTimestamp)}` : "EEX price time unavailable"}
                {receivedAt ? ` · app received ${formatUtcTimestamp(receivedAt)}` : " · app receipt time unavailable"}
              </div>
              <div className="mt-1 text-xs text-muted">
                A dash means EEX did not provide a bid or ask. Older prices stay visible and are labelled above.
              </div>
            </div>

            {!collapsed && (
              <div id={tableId} className="min-w-0">
                {rows.length === 0 ? (
                  <div className="px-3 py-5 text-center text-xs text-muted">
                    No listed instruments match the configured delivery periods.
                  </div>
                ) : (
                  <table className="w-full table-fixed border-collapse font-mono text-xs tabular-nums">
                    <caption className="sr-only">{group.label} delivery prices and quantities</caption>
                    <colgroup>
                      <col className="w-[20%]" />
                      <col className="w-[20%]" />
                      <col className="w-[20%]" />
                      <col className="w-[20%]" />
                      <col className="w-[20%]" />
                    </colgroup>
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wide">
                        <th scope="col" className="h-9 border-b border-border px-1 text-left font-sans font-medium text-muted">Delivery</th>
                        <th scope="col" className="h-9 border-b border-border bg-sky-500/10 px-1 text-right font-sans font-medium text-sky-700 dark:text-sky-300">Bid</th>
                        <th scope="col" className="h-9 border-b border-border bg-rose-500/10 px-1 text-right font-sans font-medium text-rose-700 dark:text-rose-300">Ask</th>
                        <th scope="col" className="h-9 border-b border-border px-1 text-right font-sans font-medium text-fg">Last</th>
                        <th scope="col" className="h-9 border-b border-border bg-violet-500/10 px-1 text-right font-sans font-medium text-violet-800 dark:text-violet-200">Settle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(({ period, cell }) => {
                        const quote = cell.quote;
                        const settlement = cell.settlement;
                        const rowKey = `${group.key}:${period.key}`;
                        const detailId = `eex-mobile-row-${groupIndex}-${selectedPeriods.indexOf(period)}`;
                        const expanded = expandedRows.has(rowKey);
                        const baseTitle = quote
                          ? eexMarketTimestampTitle(quote)
                          : `${cell.instrument.displayName ?? cell.instrument.providerKey} · listed by EEX · no cached top-of-book observation`;
                        const settlementPrice = settlement?.price ?? quote?.settlementPrice ?? null;

                        return (
                          <Fragment key={period.key}>
                            <tr>
                              <th scope="row" className="border-b border-border/60 p-0 text-left font-sans">
                                <button
                                  type="button"
                                  aria-expanded={expanded}
                                  aria-controls={detailId}
                                  onClick={() => toggleRow(rowKey)}
                                  className="min-h-11 w-full touch-manipulation break-words px-1 py-1 text-left text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                                >
                                  {period.label}
                                  <span className="block text-[9px] font-normal text-muted">{expanded ? "Hide details" : "Details"}</span>
                                </button>
                              </th>
                              <MobileQuoteCell price={quote?.bidPrice ?? null} lots={quote?.bidLots ?? null} tone="bid" />
                              <MobileQuoteCell price={quote?.askPrice ?? null} lots={quote?.askLots ?? null} tone="ask" />
                              <MobileLastCell quote={quote} />
                              <MobileSettlementValue price={settlementPrice} />
                            </tr>
                            {expanded && (
                              <tr id={detailId}>
                                <td colSpan={5} className="border-b border-border bg-border/[0.04] px-2 py-2 font-sans text-xs text-muted">
                                  <div className="space-y-1 break-words">
                                    <p><span className="font-medium text-fg">Delivery:</span> {period.kind} · {period.deliveryStart ?? "—"} to {period.deliveryEnd ?? "—"}</p>
                                    <p><span className="font-medium text-fg">Instrument:</span> {cell.instrument.displayName ?? cell.instrument.providerKey}</p>
                                    {cell.instrument.displayName && <p className="break-all"><span className="font-medium text-fg">Provider:</span> {cell.instrument.providerKey}</p>}
                                    <p><span className="font-medium text-fg">Top of book:</span> {baseTitle}</p>
                                    <p><span className="font-medium text-fg">Last:</span> {matrixCellTitle(quote, settlement, 4, baseTitle)}</p>
                                    <p><span className="font-medium text-fg">Settlement:</span> {matrixCellTitle(quote, settlement, 6, baseTitle)}</p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function MobileQuoteCell({
  price,
  lots,
  tone,
}: {
  price: string | null;
  lots: string | null;
  tone: "bid" | "ask";
}) {
  return (
    <td className={cn(
      "overflow-hidden border-b border-border/60 px-1 py-1 text-right align-middle",
      tone === "bid" ? "bg-sky-500/[0.06] text-sky-700 dark:text-sky-300" : "bg-rose-500/[0.06] text-rose-700 dark:text-rose-300"
    )}>
      <div className="break-all font-semibold">{eexCompactDecimal(price, 3)}</div>
      <div className="break-words font-sans text-[9px]">{eexCompactDecimal(lots, 2)} MW</div>
    </td>
  );
}

function MobileLastCell({ quote }: { quote: TopOfBook | null }) {
  const hasLastEvidence = quote?.lastTradePrice != null || quote?.lastTradeLots != null;
  return (
    <td className="overflow-hidden border-b border-border/60 px-1 py-1 text-right align-middle text-fg">
      <div className="break-all font-semibold">{eexCompactDecimal(quote?.lastTradePrice ?? null, 3)}</div>
      <div className="break-words font-sans text-[9px] text-muted">
        {hasLastEvidence
          ? `${eexCompactDecimal(quote?.lastTradeLots ?? null, 2)} MW · ${lastTradeAgeLabel(quote!)} ago`
          : "—"}
      </div>
    </td>
  );
}

function MobileSettlementValue({ price }: { price: string | null }) {
  return (
    <td className="overflow-hidden border-b border-border/60 bg-violet-500/[0.05] px-1 py-1 text-right align-middle text-violet-800 dark:text-violet-200">
      <div className="break-all font-semibold">{eexCompactDecimal(price, 3)}</div>
    </td>
  );
}

function TopOfBookTable({
  filter,
  canAdminister,
  storageScopeIds,
  historyScopeIds,
}: {
  filter: EexLiveMarketFilter;
  canAdminister: boolean;
  storageScopeIds: ReadonlySet<string>;
  historyScopeIds: ReadonlySet<string>;
}) {
  const canManageWatchlist = useEexCapability("market_data.personal");
  const queryClient = useQueryClient();
  const [displayMode, setDisplayMode] = useState<MarketBoardMode>("matrix");
  const [search, setSearch] = useState("");
  const [watchlistFilter, setWatchlistFilter] = useState<EexMarketWatchlistFilter>("all");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const { query, instrumentsQ, settlementsQ } = useTopOfBookQueries(filter);
  const companyUniverseQ = useQuery({
    // Keep this lightweight observer separate from the editor's full response
    // cache; sharing a key across different response shapes would be unsafe.
    queryKey: ["eex-market-data", "company-live-universe-mode"],
    queryFn: () => apiContract(
      EexLiveUniverseResponseSchema,
      "/eex/market-data/preferences/live-universe"
    ),
    enabled: canAdminister,
    retry: false,
    staleTime: 60_000,
  });
  const companyWatchlistManaged = companyUniverseQ.data?.mode === "company_watchlist";
  const companyUniverseModeKnown = companyUniverseQ.isSuccess;
  const preferenceM = useMutation({
    mutationFn: (input:
      | { kind: "personal-watchlist"; scopeId: string; subjectId: string; enabled: boolean }
      | { kind: "company-watchlist"; scopeId: string; subjectId: string; enabled: boolean }
      | { kind: "storage"; scopeId: string; subjectId: string; priorityLevel: 0 | 1 | 2 | 3 }
    ) => {
      if (input.kind === "personal-watchlist") return apiContract(
        EexSubjectPreferenceMutationResponseSchema,
        "/eex/market-data/preferences/personal-watchlist",
        {
          method: "PUT",
          body: JSON.stringify(EexWatchlistUpdateRequestSchema.parse({
            scopeId: input.scopeId,
            subjectId: input.subjectId,
            enabled: input.enabled,
          })),
        }
      );
      if (input.kind === "company-watchlist") {
        if (!companyUniverseModeKnown || companyWatchlistManaged) {
          return Promise.reject(new Error("Manage the company selection through Company live universe"));
        }
        return apiContract(
          EexSubjectPreferenceMutationResponseSchema,
          "/eex/market-data/preferences/company-watchlist",
          {
            method: "PUT",
            body: JSON.stringify(EexWatchlistUpdateRequestSchema.parse({
              scopeId: input.scopeId,
              subjectId: input.subjectId,
              enabled: input.enabled,
            })),
          }
        );
      }
      return apiContract(
        EexSubjectPreferenceMutationResponseSchema,
        "/eex/market-data/preferences/storage",
        {
          method: "PUT",
          body: JSON.stringify(EexStoragePreferenceUpdateRequestSchema.parse(input.priorityLevel === 0
            ? { scopeId: input.scopeId, subjectId: input.subjectId, storageMode: "live_only" }
            : { scopeId: input.scopeId, subjectId: input.subjectId, storageMode: "snapshots", priorityLevel: input.priorityLevel })),
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["eex-market-data", "preferences"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-market-data", "top-of-book"] });
    },
  });
  const visibleRows = useMemo(
    () => (query.data?.items ?? []).filter((row) =>
      eexMarketSearchMatches(row, search) && eexMarketWatchlistMatches(row, watchlistFilter)
    ),
    [query.data?.items, search, watchlistFilter]
  );
  const allRows = query.data?.items ?? [];
  const settlementByKey = useMemo(
    () => new Map(
      (settlementsQ.data?.items ?? []).map((settlement) => [marketRowKey(settlement), settlement])
    ),
    [settlementsQ.data?.items]
  );
  const matrixInstruments = instrumentsQ.data?.items ?? [];
  const activeSelectedKey = selectedKey && (
    allRows.some((row) => marketRowKey(row) === selectedKey)
    || matrixInstruments.some((row) => marketRowKey(row) === selectedKey)
  )
    ? selectedKey
    : visibleRows[0]
      ? marketRowKey(visibleRows[0])
      : null;
  const selectedRow = allRows.find((row) => marketRowKey(row) === activeSelectedKey) ?? null;
  const selectedInstrument = matrixInstruments
    .find((row) => marketRowKey(row) === activeSelectedKey) ?? null;
  const selectedTapeSubject: TradeTapeSubject | null = selectedRow ?? (selectedInstrument ? {
    scopeId: selectedInstrument.scopeId,
    subjectId: selectedInstrument.subjectId,
    apiFamily: selectedInstrument.apiFamily,
    commodityCode: selectedInstrument.commodityCode,
    areaCode: selectedInstrument.areaCode,
    providerKey: selectedInstrument.providerKey,
    displayName: selectedInstrument.displayName,
    lastTradeAt: null,
  } : null);

  if (query.isLoading) return <div className="py-6 text-center text-xs text-muted">Loading cached live instruments…</div>;
  if (query.isError) {
    return <div className="py-6 text-center text-xs text-negRed">{query.error?.message ?? "Cached data request failed"}</div>;
  }

  if (displayMode === "curve") {
    return (
      <div className="space-y-3">
        <BoardModeSwitch mode={displayMode} onChange={setDisplayMode} />
        {instrumentsQ.isLoading ? (
          <div className="py-8 text-center text-xs text-muted">Loading the EEX delivery-period catalogue…</div>
        ) : instrumentsQ.isError ? (
          <CatalogueLoadError
            message="The curve catalogue could not be loaded. Live instrument viewing remains available."
            retrying={instrumentsQ.isFetching}
            onRetry={() => void instrumentsQ.refetch()}
          />
        ) : (
          <ForwardCurveBoard
            instruments={matrixInstruments}
            quotes={allRows}
            historyScopeIds={historyScopeIds}
          />
        )}
      </div>
    );
  }

  if (displayMode === "matrix") {
    return (
      <div className="space-y-3">
        <BoardModeSwitch mode={displayMode} onChange={setDisplayMode} />
        {instrumentsQ.isLoading ? (
          <div className="py-8 text-center text-xs text-muted">Loading the EEX delivery-period catalogue…</div>
        ) : instrumentsQ.isError ? (
          <CatalogueLoadError
            message="The board catalogue could not be loaded. The instrument catalogue remains available."
            retrying={instrumentsQ.isFetching}
            onRetry={() => void instrumentsQ.refetch()}
          />
        ) : (
          <MarketMatrixBoard
            instruments={matrixInstruments}
            quotes={allRows}
            settlements={settlementsQ.data?.items ?? []}
            selectedKey={activeSelectedKey}
            onSelect={setSelectedKey}
          />
        )}
        {settlementsQ.isError && (
          <div className="rounded border border-amber-500/30 px-3 py-2 text-[10px] text-amber-700 dark:text-amber-300">
            Official settlement marks could not be refreshed. Live bid/ask and Last remain available.
          </div>
        )}
        <InstrumentTradeTape selected={selectedTapeSubject} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <BoardModeSwitch mode={displayMode} onChange={setDisplayMode} />
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-xs font-semibold">Instrument watchboard</div>
          <div className="text-[10px] text-muted">
            Search the currently available instruments, star the ones you want to watch, then select a row for its public trade tape.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <label className="relative">
            <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
            <span className="sr-only">Search available instruments</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search instrument or market"
              className="h-7 w-56 rounded border border-border bg-transparent pl-7 pr-2 text-xs focus:border-accent focus:outline-none"
            />
          </label>
          <WatchlistFilterButton
            active={watchlistFilter === "all"}
            onClick={() => setWatchlistFilter("all")}
            label="All instruments"
          />
          <WatchlistFilterButton
            active={watchlistFilter === "personal"}
            onClick={() => setWatchlistFilter("personal")}
            label="My watchlist"
          />
          {canAdminister && (
            <WatchlistFilterButton
              active={watchlistFilter === "company"}
              onClick={() => setWatchlistFilter("company")}
              label="Company"
            />
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-3 text-[10px] text-muted">
        <span>{visibleRows.length} shown</span>
        <span>{query.data?.items.length ?? 0} currently available</span>
        {query.data?.truncated && <span className="text-amber-700 dark:text-amber-300">Result capped; narrow the market filters.</span>}
      </div>
      {preferenceM.isError && (
        <div className="rounded border border-negRed/30 px-3 py-2 text-xs text-negRed">
          Unable to update the market preference: {(preferenceM.error as Error).message}
        </div>
      )}

      {visibleRows.length === 0 ? (
        <div className="rounded border border-border px-3 py-8 text-center text-xs text-muted">
          {watchlistFilter === "personal"
            ? "Your watchlist is empty for these filters. Choose All instruments and click a star to add one."
            : watchlistFilter === "company"
              ? "The company watchlist is empty for these filters."
              : search.trim()
                ? "No currently available instrument matches this search."
                : "No cached best bid/ask rows match these filters."}
        </div>
      ) : (
        <div className="max-h-[34rem] overflow-auto rounded border border-border/70" role="region" aria-label="Live EEX instrument watchboard" tabIndex={0}>
          <table className="min-w-[1040px] w-full border-separate border-spacing-0 font-mono text-[11px]">
            <thead className="text-[10px] uppercase tracking-wide text-muted">
              <tr>
                <th className="sticky left-0 top-0 z-30 w-9 border-b border-border bg-bg px-1 py-1.5 text-center font-medium">Watch</th>
                <th className="sticky left-9 top-0 z-30 min-w-56 border-b border-border bg-bg px-2 py-1.5 text-left font-medium">Instrument</th>
                <th className="sticky top-0 z-20 border-b border-border bg-bg px-2 py-1.5 text-left font-medium">Market</th>
                <th className="sticky top-0 z-20 border-b border-border bg-sky-500/10 px-2 py-1.5 text-right font-medium text-sky-700 dark:text-sky-300">Bid MW</th>
                <th className="sticky top-0 z-20 border-b border-border bg-sky-500/10 px-2 py-1.5 text-right font-medium text-sky-700 dark:text-sky-300">Bid</th>
                <th className="sticky top-0 z-20 border-b border-border bg-rose-500/10 px-2 py-1.5 text-right font-medium text-rose-700 dark:text-rose-300">Ask</th>
                <th className="sticky top-0 z-20 border-b border-border bg-rose-500/10 px-2 py-1.5 text-right font-medium text-rose-700 dark:text-rose-300">Ask MW</th>
                <th className="sticky top-0 z-20 hidden border-b border-border bg-bg px-2 py-1.5 text-right font-medium 2xl:table-cell">Mid</th>
                <th className="sticky top-0 z-20 hidden border-b border-border bg-bg px-2 py-1.5 text-right font-medium 2xl:table-cell">Spread</th>
                <th className="sticky top-0 z-20 border-b border-border bg-bg px-2 py-1.5 text-right font-medium">Last</th>
                <th className="sticky top-0 z-20 border-b border-border bg-bg px-2 py-1.5 text-right font-medium">Last MW</th>
                <th className="sticky top-0 z-20 border-b border-border bg-violet-500/10 px-2 py-1.5 text-right font-medium text-violet-700 dark:text-violet-300">Settlement</th>
                <th className="sticky top-0 z-20 border-b border-border bg-bg px-2 py-1.5 text-left font-medium">Feed</th>
                {canAdminister && <th className="sticky top-0 z-20 border-b border-border bg-bg px-2 py-1.5 text-left font-medium">
                  {companyWatchlistManaged ? "Company universe / history" : "Company / history"}
                </th>}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const key = marketRowKey(row);
                const selected = key === activeSelectedKey;
                return (
                  <tr
                    key={key}
                    tabIndex={0}
                    aria-selected={selected}
                    onClick={() => setSelectedKey(key)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedKey(key);
                      }
                    }}
                    className={cn(
                      "cursor-pointer outline-none hover:bg-border/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent",
                      selected && "bg-accent/[0.07]"
                    )}
                  >
                    <td className={cn("sticky left-0 z-10 border-t border-border/60 px-1 py-1 text-center", selected ? "bg-accent/[0.07]" : "bg-bg")}>
                      <button
                        type="button"
                        disabled={!canManageWatchlist || preferenceM.isPending}
                        aria-label={row.personalWatchlisted
                          ? `Remove ${row.displayName ?? row.providerKey} from my watchlist`
                          : `Add ${row.displayName ?? row.providerKey} to my watchlist`}
                        title={row.personalWatchlisted ? "Remove from my watchlist" : "Add to my watchlist"}
                        onClick={(event) => {
                          event.stopPropagation();
                          preferenceM.mutate({
                            kind: "personal-watchlist",
                            scopeId: row.scopeId,
                            subjectId: row.subjectId,
                            enabled: !row.personalWatchlisted,
                          });
                        }}
                        className={cn(
                          "rounded p-1 hover:bg-border/40",
                          row.personalWatchlisted ? "text-amber-500" : "text-muted"
                        )}
                      >
                        <Star size={13} fill={row.personalWatchlisted ? "currentColor" : "none"} />
                      </button>
                    </td>
                    <td className={cn("sticky left-9 z-10 max-w-72 border-t border-border/60 px-2 py-1", selected ? "bg-accent/[0.07]" : "bg-bg")} title={row.providerKey}>
                      <div className="truncate font-sans text-xs font-medium">{row.displayName ?? row.providerKey}</div>
                      {row.displayName && <div className="truncate text-[9px] text-muted">{row.providerKey}</div>}
                    </td>
                    <Cell primary={row.areaCode} secondary={`${row.commodityCode} · ${row.apiFamily}`} />
                    <CompactSizeCell lots={row.bidLots} tone="bid" />
                    <CompactPriceCell value={row.bidPrice} tone="bid" />
                    <CompactPriceCell value={row.askPrice} tone="ask" />
                    <CompactSizeCell lots={row.askLots} tone="ask" />
                    <CompactPriceCell value={row.midpoint} className="hidden 2xl:table-cell" />
                    <CompactPriceCell value={eexMarketSpread(row.askPrice, row.bidPrice)} className="hidden 2xl:table-cell" />
                    <CompactPriceCell value={row.lastTradePrice} />
                    <CompactSizeCell lots={row.lastTradeLots} />
                    <SettlementCell row={row} settlement={settlementByKey.get(key) ?? null} />
                    <td
                      className="whitespace-nowrap border-t border-border/60 px-2 py-1 font-sans"
                      title={[
                        `top-of-book scope ${row.scopeId}`,
                        row.marketTimestamp ? `market ${row.marketTimestamp}` : null,
                        `received ${row.receivedAt}`,
                        row.lastTradeAt ? `last trade ${row.lastTradeAt}` : null,
                        row.lastTradeReceivedAt ? `last trade received ${row.lastTradeReceivedAt}` : null,
                        row.lastTradePrice != null
                          ? "last-trade age is independent of top-of-book feed freshness"
                          : null,
                      ].filter(Boolean).join(" · ")}
                    >
                      <StateBadge value={row.freshnessState} detail={row.coverageState} />
                      <div className="mt-0.5 text-[9px] text-muted">{eexObservationAgeLabel(row.receivedAt)} ago · EEX TOB</div>
                      <div className="text-[9px] text-muted">
                        {row.lastTradePrice != null
                          ? `Last trade ${lastTradeAgeLabel(row)} ago · EEX public`
                          : "Last trade: unavailable"}
                      </div>
                    </td>
                    {canAdminister && (
                      <td className="border-t border-border/60 px-2 py-1">
                        <div className="flex items-center gap-1" onClick={(event) => event.stopPropagation()}>
                          {companyUniverseQ.isLoading ? (
                            <span className="font-sans text-[9px] text-muted">Loading company mode…</span>
                          ) : companyUniverseQ.isError ? (
                            <span className="font-sans text-[9px] text-negRed">Company mode unavailable</span>
                          ) : companyWatchlistManaged ? (
                            <button
                              type="button"
                              title="Use Company live universe to change the shared acquisition selection"
                              onClick={() => {
                                const panel = document.getElementById(
                                  "eex-company-live-universe"
                                ) as HTMLDetailsElement | null;
                                if (!panel) return;
                                panel.open = true;
                                panel.scrollIntoView({ behavior: "smooth", block: "start" });
                              }}
                              className="rounded border border-border px-1.5 py-1 font-sans text-[9px] text-accent"
                            >
                              Manage company live universe
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={preferenceM.isPending}
                              aria-label={row.companyWatchlisted
                                ? `Remove ${row.displayName ?? row.providerKey} from company watchlist`
                                : `Add ${row.displayName ?? row.providerKey} to company watchlist`}
                              title={row.companyWatchlisted ? "Remove from company watchlist" : "Add to company watchlist"}
                              onClick={() => preferenceM.mutate({
                                kind: "company-watchlist",
                                scopeId: row.scopeId,
                                subjectId: row.subjectId,
                                enabled: !row.companyWatchlisted,
                              })}
                              className={cn(
                                "flex items-center gap-1 rounded border border-border px-1.5 py-1 font-sans text-[9px]",
                                row.companyWatchlisted ? "text-amber-500" : "text-muted"
                              )}
                            >
                              <Star size={11} fill={row.companyWatchlisted ? "currentColor" : "none"} /> Company
                            </button>
                          )}
                          {storageScopeIds.has(row.scopeId) ? (
                            <select
                              aria-label={`Snapshot priority for ${row.displayName ?? row.providerKey}`}
                              value={row.storageMode === "snapshots" ? row.priorityLevel ?? 0 : 0}
                              disabled={preferenceM.isPending}
                              onChange={(event) => preferenceM.mutate({
                                kind: "storage",
                                scopeId: row.scopeId,
                                subjectId: row.subjectId,
                                priorityLevel: Number(event.target.value) as 0 | 1 | 2 | 3,
                              })}
                              className="h-6 rounded border border-border bg-transparent px-1 font-sans text-[9px]"
                            >
                              <option value={0}>live only</option>
                              <option value={1}>L1 · 5m</option>
                              <option value={2}>L2 · 15m</option>
                              <option value={3}>L3 · 1h</option>
                            </select>
                          ) : (
                            <span className="font-sans text-[9px] text-muted">live view</span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <InstrumentTradeTape selected={selectedTapeSubject} />
    </div>
  );
}

function BoardModeSwitch({
  mode,
  onChange,
}: {
  mode: MarketBoardMode;
  onChange: (mode: MarketBoardMode) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <div className="text-xs font-semibold">Market board</div>
        <div className="text-[10px] text-muted">
          Compare live and historical curves, trade from the matrix, or open the catalogue for instrument controls.
        </div>
      </div>
      <div className="flex items-center gap-1 rounded border border-border p-0.5">
        <button
          type="button"
          onClick={() => onChange("matrix")}
          className={cn(
            "flex h-7 items-center gap-1 rounded px-2 text-[10px]",
            mode === "matrix" ? "bg-accent/15 text-accent" : "text-muted hover:text-fg"
          )}
        >
          <LayoutGrid size={12} /> Trading matrix
        </button>
        <button
          type="button"
          onClick={() => onChange("curve")}
          className={cn(
            "flex h-7 items-center gap-1 rounded px-2 text-[10px]",
            mode === "curve" ? "bg-accent/15 text-accent" : "text-muted hover:text-fg"
          )}
        >
          <LineChart size={12} /> Curve comparison
        </button>
        <button
          type="button"
          onClick={() => onChange("catalogue")}
          className={cn(
            "flex h-7 items-center gap-1 rounded px-2 text-[10px]",
            mode === "catalogue" ? "bg-accent/15 text-accent" : "text-muted hover:text-fg"
          )}
        >
          <List size={12} /> Instrument catalogue
        </button>
      </div>
    </div>
  );
}

function useResolvedMarketMatrix(
  instruments: MatrixInstrument[],
  quotes: TopOfBook[],
  settlements: SettlementMark[]
) {
  const matrix = useMemo(
    () => buildEexMarketMatrix(instruments, quotes, settlements),
    [instruments, quotes, settlements]
  );
  const defaults = useMemo(() => eexDefaultMatrixConfiguration(matrix), [matrix]);
  const storageKey = useMemo(() => {
    const scopes = [...new Set(instruments.map((row) => row.scopeId))].sort().join(".");
    return `cascena:eex-market-matrix:v2:${scopes || "unscoped"}`;
  }, [instruments]);
  const legacyStorageKey = useMemo(() => storageKey.replace(":v2:", ":v1:"), [storageKey]);
  const matrixSignature = useMemo(
    () => `${matrix.groups.map((group) => group.key).join("|")}::${matrix.periods.map((period) => period.key).join("|")}`,
    [matrix]
  );
  const [configuration, setConfiguration] = useState<EexMatrixBoardConfiguration | null>(null);
  const [storageError, setStorageError] = useState(false);
  const saveConfiguration = (next: EexMatrixBoardConfiguration) => {
    setConfiguration(next);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
      setStorageError(false);
    } catch {
      setStorageError(true);
    }
  };
  const restoreDefaults = () => saveConfiguration(defaults);

  useEffect(() => {
    let next = defaults;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        next = sanitizeMatrixConfiguration(JSON.parse(raw), matrix, defaults);
      } else {
        const legacyRaw = window.localStorage.getItem(legacyStorageKey);
        if (legacyRaw) {
          const legacy = JSON.parse(legacyRaw) as Record<string, unknown>;
          // A v1 list was an explicit set of rows. Preserve that user intent as
          // custom instead of silently converting it to the new rolling mode.
          next = sanitizeMatrixConfiguration({ ...legacy, periodMode: "custom" }, matrix, defaults);
        }
      }
    } catch {
      // A blocked or malformed local preference must not prevent live viewing.
    }
    setConfiguration(next);
  }, [storageKey, legacyStorageKey, matrixSignature]); // defaults and matrix are represented by the stable signature

  const preference = configuration ?? defaults;
  const active = preference.periodMode === "follow_universe"
    ? { ...preference, deliveryPeriodKeys: eexDefaultMatrixDeliveryPeriodKeys(matrix.periods) }
    : {
      ...preference,
      deliveryPeriodKeys: eexCustomMatrixDeliveryPeriodKeys(matrix.periods, preference.deliveryPeriodKeys),
    };

  return {
    matrix,
    defaults,
    configuration,
    setConfiguration,
    storageKey,
    matrixSignature,
    active,
    saveConfiguration,
    restoreDefaults,
    storageError,
    missingGroupCount: active.productGroupKeys.filter((key) => !matrix.groups.some((group) => group.key === key)).length,
  };
}

function MarketBoardRecovery({
  missingGroupCount,
  omittedDefaultCount,
  storageError,
  onRestore,
}: {
  missingGroupCount: number;
  omittedDefaultCount: number;
  storageError: boolean;
  onRestore: () => void;
}) {
  return (
    <div className="space-y-2 rounded border border-border px-3 py-2 text-sm">
      {missingGroupCount > 0 ? (
        <p role="status">{missingGroupCount} saved market selections are unavailable in the current catalogue. They remain saved and will reappear if available again.</p>
      ) : omittedDefaultCount > 0 ? (
        <p>Your saved board omits {omittedDefaultCount} available default markets. The filters above do not reset your board selection.</p>
      ) : null}
      <button type="button" onClick={onRestore} className="min-h-11 rounded border border-border px-3 py-2 text-accent hover:border-accent">
        Restore market defaults
      </button>
      <p className="text-xs text-muted">Restores the default markets and delivery periods for these filters in this browser.</p>
      {storageError && <p role="alert">The board changed, but this browser could not save it. Reloading may restore the previous layout.</p>}
    </div>
  );
}

function MarketMatrixBoard({
  instruments,
  quotes,
  settlements,
  selectedKey,
  onSelect,
}: {
  instruments: MatrixInstrument[];
  quotes: TopOfBook[];
  settlements: SettlementMark[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}) {
  const {
    matrix,
    defaults,
    saveConfiguration,
    restoreDefaults,
    missingGroupCount,
    storageError,
    storageKey,
    matrixSignature,
    active,
  } = useResolvedMarketMatrix(instruments, quotes, settlements);
  const [draft, setDraft] = useState<EexMatrixBoardConfiguration | null>(null);
  const [configureOpen, setConfigureOpen] = useState(false);

  useEffect(() => {
    setDraft(null);
  }, [storageKey, matrixSignature]);
  const groupByKey = new Map(matrix.groups.map((group) => [group.key, group]));
  const periodKeys = new Set(active.deliveryPeriodKeys);
  const selectedGroups = active.productGroupKeys.flatMap((key) => {
    const group = groupByKey.get(key);
    return group ? [group] : [];
  });
  const selectedPeriods = matrix.periods.filter((period) => periodKeys.has(period.key));

  const openConfiguration = () => {
    setDraft({
      productGroupKeys: [...active.productGroupKeys],
      periodMode: active.periodMode,
      deliveryPeriodKeys: active.periodMode === "custom" ? [...active.deliveryPeriodKeys] : [],
    });
    setConfigureOpen(true);
  };
  const applyConfiguration = () => {
    if (!draft) return;
    const next = sanitizeMatrixConfiguration(draft, matrix, defaults);
    saveConfiguration(next);
    setConfigureOpen(false);
  };

  if (matrix.groups.length === 0 || matrix.periods.length === 0) {
    return (
      <div className="rounded border border-border px-3 py-8 text-center text-xs text-muted">
        No outright futures or gas contracts are currently available for the selected market filters.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-x-3 text-[10px] text-muted">
          <span>{selectedGroups.length} product groups</span>
          <span>{selectedPeriods.length} delivery periods</span>
          <span>{active.periodMode === "follow_universe" ? "Market default rows" : "Custom rows"}</span>
          <span>Prices update in place</span>
        </div>
        <button
          type="button"
          onClick={openConfiguration}
          className="flex h-7 items-center gap-1 rounded border border-border px-2 text-[10px] text-muted hover:border-accent hover:text-fg"
        >
          <Settings2 size={12} /> Configure board
        </button>
      </div>

      {(missingGroupCount > 0 || storageError) && (
        <MarketBoardRecovery missingGroupCount={missingGroupCount} omittedDefaultCount={0} storageError={storageError} onRestore={() => { restoreDefaults(); setConfigureOpen(false); }} />
      )}
      {configureOpen && draft && (
        <MatrixConfigurator
          matrix={matrix}
          draft={draft}
          onChange={setDraft}
          onReset={() => setDraft({
            ...defaults,
            productGroupKeys: [...defaults.productGroupKeys],
            deliveryPeriodKeys: [...defaults.deliveryPeriodKeys],
          })}
          onCancel={() => setConfigureOpen(false)}
          onApply={applyConfiguration}
        />
      )}

      {selectedGroups.length === 0 || selectedPeriods.length === 0 ? (
        <button
          type="button"
          onClick={openConfiguration}
          className="w-full rounded border border-dashed border-border px-3 py-10 text-center text-xs text-muted hover:border-accent hover:text-fg"
        >
          Select at least one product and one delivery period in Configure board.
        </button>
      ) : (
        <MatrixTable
          groups={selectedGroups}
          periods={selectedPeriods}
          selectedKey={selectedKey}
          onSelect={onSelect}
        />
      )}
    </div>
  );
}

type MatrixModel = ReturnType<
  typeof buildEexMarketMatrix<MatrixInstrument, TopOfBook, SettlementMark>
>;

function MatrixConfigurator({
  matrix,
  draft,
  onChange,
  onReset,
  onCancel,
  onApply,
}: {
  matrix: MatrixModel;
  draft: EexMatrixBoardConfiguration;
  onChange: (next: EexMatrixBoardConfiguration) => void;
  onReset: () => void;
  onCancel: () => void;
  onApply: () => void;
}) {
  const selectedGroups = new Set(draft.productGroupKeys);
  const selectedPeriods = new Set(draft.deliveryPeriodKeys);
  const groupedPeriods = groupPeriods(matrix.periods);
  const toggleGroup = (key: string) => {
    const next = selectedGroups.has(key)
      ? draft.productGroupKeys.filter((candidate) => candidate !== key)
      : [...draft.productGroupKeys, key];
    onChange({ ...draft, productGroupKeys: next });
  };
  const moveGroup = (key: string, direction: -1 | 1) => {
    const next = [...draft.productGroupKeys];
    const index = next.indexOf(key);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange({ ...draft, productGroupKeys: next });
  };
  const togglePeriod = (key: string) => {
    const next = selectedPeriods.has(key)
      ? draft.deliveryPeriodKeys.filter((candidate) => candidate !== key)
      : [...draft.deliveryPeriodKeys, key];
    onChange({ ...draft, deliveryPeriodKeys: next });
  };
  const selectPreset = (kind: EexMatrixDeliveryPeriod["kind"], limit: number) => {
    const keys = matrix.periods.filter((period) => period.kind === kind).slice(0, limit).map((period) => period.key);
    onChange({
      ...draft,
      deliveryPeriodKeys: [...new Set([...draft.deliveryPeriodKeys, ...keys])],
    });
  };

  return (
    <section className="rounded border border-accent/40 bg-bg shadow-lg" aria-label="Configure market board">
      <div className="flex items-start justify-between gap-2 border-b border-border px-3 py-2">
        <div>
          <div className="text-xs font-semibold">Configure market board</div>
          <div className="text-[10px] text-muted">Display only: choose product columns and rows without changing the company universe or acquisition.</div>
        </div>
        <button type="button" aria-label="Close board configuration" onClick={onCancel} className="rounded p-1 text-muted hover:bg-border/30 hover:text-fg">
          <X size={14} />
        </button>
      </div>
      <div className="grid max-h-[30rem] gap-0 overflow-auto lg:grid-cols-[minmax(18rem,0.8fr)_minmax(28rem,1.4fr)]">
        <div className="border-b border-border p-3 lg:border-b-0 lg:border-r">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted">Products / column order</div>
          <div className="space-y-1">
            {matrix.groups.map((group) => {
              const checked = selectedGroups.has(group.key);
              const position = draft.productGroupKeys.indexOf(group.key);
              return (
                <div key={group.key} className={cn("flex items-center gap-2 rounded border px-2 py-1.5", checked ? "border-accent/50 bg-accent/5" : "border-border/70")}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleGroup(group.key)}
                    aria-label={`Show ${group.label}`}
                  />
                  <button type="button" onClick={() => toggleGroup(group.key)} className="min-w-0 flex-1 text-left">
                    <div className="truncate text-[11px] font-medium">{group.label}</div>
                    <div className="truncate font-mono text-[9px] text-muted">
                      {group.areaCode} · {group.familyCode ?? group.commodityCode} · {group.currency ?? "—"}/{group.uom ?? "—"}
                    </div>
                  </button>
                  {checked && (
                    <div className="flex items-center">
                      <button type="button" aria-label={`Move ${group.label} left`} disabled={position <= 0} onClick={() => moveGroup(group.key, -1)} className="rounded p-1 text-muted hover:bg-border/30 disabled:opacity-25"><ChevronLeft size={12} /></button>
                      <button type="button" aria-label={`Move ${group.label} right`} disabled={position < 0 || position >= draft.productGroupKeys.length - 1} onClick={() => moveGroup(group.key, 1)} className="rounded p-1 text-muted hover:bg-border/30 disabled:opacity-25"><ChevronRight size={12} /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-3">
          <fieldset className="mb-3 rounded border border-border p-2">
            <legend className="px-1 text-[9px] font-semibold uppercase tracking-wide text-muted">Row mode</legend>
            <div className="flex flex-wrap gap-4 text-[10px]">
              <label className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="radio"
                  name="eex-market-board-row-mode"
                  checked={draft.periodMode === "follow_universe"}
                  onChange={() => onChange({ ...draft, periodMode: "follow_universe", deliveryPeriodKeys: [] })}
                />
                Follow market defaults
              </label>
              <label className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="radio"
                  name="eex-market-board-row-mode"
                  checked={draft.periodMode === "custom"}
                  onChange={() => onChange({
                    ...draft,
                    periodMode: "custom",
                    deliveryPeriodKeys: eexDefaultMatrixDeliveryPeriodKeys(matrix.periods),
                  })}
                />
                Custom rows
              </label>
            </div>
            <p className="mt-1 text-[9px] text-muted">
              Both modes roll past expired delivery periods. Market defaults use 11 days, 2 weekends, 3 weeks,
              7 months, 6 quarters, 3 seasons and 3 years when available; custom rows keep your selected tenors
              and row counts as they advance.
            </p>
          </fieldset>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted">Delivery periods / rows</div>
            <div className={cn("flex flex-wrap gap-1", draft.periodMode !== "custom" && "pointer-events-none opacity-40")}>
              <PresetButton label="Next 12 months" onClick={() => selectPreset("month", 12)} />
              <PresetButton label="Next 8 quarters" onClick={() => selectPreset("quarter", 8)} />
              <PresetButton label="Next 5 years" onClick={() => selectPreset("year", 5)} />
            </div>
          </div>
          {draft.periodMode === "follow_universe" ? (
            <div className="mt-3 rounded border border-dashed border-border px-3 py-5 text-center text-[10px] text-muted">
              Compact default rows are recomputed from the current company universe as delivery periods roll.
            </div>
          ) : <div className="mt-2 space-y-3">
            {groupedPeriods.map(([kind, periods]) => (
              <div key={kind}>
                <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-muted">{kind}</div>
                <div className="flex flex-wrap gap-1">
                  {periods.map((period) => (
                    <label key={period.key} className={cn(
                      "flex cursor-pointer items-center gap-1 rounded border px-1.5 py-1 font-mono text-[10px]",
                      selectedPeriods.has(period.key) ? "border-accent/60 bg-accent/10 text-fg" : "border-border/70 text-muted"
                    )}>
                      <input type="checkbox" className="sr-only" checked={selectedPeriods.has(period.key)} onChange={() => togglePeriod(period.key)} />
                      {period.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-3 py-2">
        <button type="button" onClick={onReset} className="text-[10px] text-muted hover:text-fg">Reset to market defaults</button>
        <div className="flex gap-1.5">
          <button type="button" onClick={onCancel} className="h-7 rounded border border-border px-3 text-[10px] text-muted">Cancel</button>
          <button type="button" onClick={onApply} className="h-7 rounded border border-accent bg-accent/15 px-3 text-[10px] font-medium text-accent">Apply board</button>
        </div>
      </div>
    </section>
  );
}

function PresetButton({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" onClick={onClick} className="rounded border border-border px-1.5 py-1 text-[9px] text-muted hover:border-accent hover:text-fg">{label}</button>;
}

function MatrixTable({
  groups,
  periods,
  selectedKey,
  onSelect,
}: {
  groups: MatrixModel["groups"];
  periods: EexMatrixDeliveryPeriod[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="max-h-[42rem] overflow-auto rounded border border-border/80" role="region" aria-label="EEX trading matrix" tabIndex={0}>
      <table
        className="w-full border-separate border-spacing-0 font-mono text-[10px]"
        style={{ minWidth: `${132 + groups.length * 420}px` }}
      >
        <thead>
          <tr>
            <th rowSpan={2} className="sticky left-0 top-0 z-50 w-[132px] min-w-[132px] border-b border-r border-border bg-bg px-2 py-1.5 text-left font-sans text-[10px] font-semibold uppercase tracking-wide text-muted">
              Delivery
            </th>
            {groups.map((group) => {
              const visibleQuotes = periods.flatMap((period) => {
                const quote = group.cells.get(period.key)?.quote;
                return quote ? [quote] : [];
              });
              const marketTimestamp = latestTimestamp(visibleQuotes.map((quote) => quote.marketTimestamp));
              const feedState = groupFeedState(visibleQuotes);
              const feedLabel = groupFeedLabel(feedState);
              return (
                <th
                  key={group.key}
                  colSpan={7}
                  title={`${group.familyCode ?? group.commodityCode} · ${visibleQuotes.length} current observations · ${feedLabel}`}
                  className="sticky top-0 z-30 h-8 border-b border-l border-border bg-bg px-2 py-1 text-center font-sans"
                >
                  <div className="flex items-center justify-center gap-1.5 whitespace-nowrap text-[11px] font-semibold">
                    <span className={cn("h-1.5 w-1.5 rounded-full", feedState === "live" ? "bg-posGreen" : feedState === "warning" ? "bg-amber-500" : "bg-negRed")} />
                    {group.label}
                  </div>
                  <div className="whitespace-nowrap text-[8px] font-normal text-muted">
                    {marketTimestamp ? `Market ${formatUtcTimestamp(marketTimestamp)}` : "No market timestamp"} · {group.familyCode ?? group.commodityCode} · {group.currency ?? "—"}/{group.uom ?? "—"} · {feedLabel}
                  </div>
                </th>
              );
            })}
          </tr>
          <tr className="uppercase tracking-wide text-muted">
            {groups.map((group) => (
              <MatrixSubheaders key={group.key} />
            ))}
          </tr>
        </thead>
        <tbody>
          {periods.map((period, index) => {
            const previous = periods[index - 1];
            const sectionStart = !previous || previous.kind !== period.kind;
            return (
              <tr key={period.key}>
                <th className={cn(
                  "sticky left-0 z-20 h-7 whitespace-nowrap border-r border-border bg-bg px-2 text-left font-sans text-[11px] font-semibold",
                  sectionStart ? "border-t-2 border-t-border" : "border-t border-t-border/50"
                )} title={`${period.kind} · ${period.deliveryStart ?? "—"} to ${period.deliveryEnd ?? "—"}`}>
                  {period.label}
                </th>
                {groups.map((group) => (
                  <MatrixQuoteCells
                    key={`${group.key}:${period.key}`}
                    cell={group.cells.get(period.key) ?? null}
                    selectedKey={selectedKey}
                    sectionStart={sectionStart}
                    onSelect={onSelect}
                  />
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MatrixSubheaders() {
  const headers = [
    ["Bid MW", "bid"], ["Bid", "bid"], ["Ask", "ask"], ["Ask MW", "ask"],
    ["Last", "plain"], ["Last MW", "plain"], ["Settle", "settlement"],
  ] as const;
  return <>{headers.map(([label, tone], index) => (
    <th
      key={label}
      title={index === 4 || index === 5
        ? "Latest EEX public-trade event; its age is independent of top-of-book feed freshness"
        : undefined}
      className={cn(
      "sticky top-8 z-30 h-7 min-w-[54px] border-b border-border px-1 py-1 text-right text-[8px] font-medium",
      index === 0 && "border-l",
      tone === "bid" && "bg-sky-500/10 text-sky-700 dark:text-sky-300",
      tone === "ask" && "bg-rose-500/10 text-rose-700 dark:text-rose-300",
      tone === "plain" && "bg-bg",
      tone === "settlement" && "bg-violet-500/10 text-violet-700 dark:text-violet-300"
      )}
    >{label}</th>
  ))}</>;
}

function MatrixQuoteCells({
  cell,
  selectedKey,
  sectionStart,
  onSelect,
}: {
  cell: MatrixModel["groups"][number]["cells"] extends Map<string, infer C> ? C | null : never;
  selectedKey: string | null;
  sectionStart: boolean;
  onSelect: (key: string) => void;
}) {
  if (!cell) {
    return (
      <td
        colSpan={7}
        title="No contract is included for this product and delivery row"
        aria-label="Outside configured universe"
        className={cn(
          "h-7 border-l border-border/30 bg-border/[0.035] px-2 text-center font-sans text-[9px] font-medium text-muted/60",
          sectionStart ? "border-t-2 border-t-border" : "border-t border-t-border/40"
        )}
      >Outside configured universe</td>
    );
  }
  const row = cell.quote;
  const settlement = cell.settlement;
  const key = marketRowKey(row ?? cell.instrument);
  const selected = selectedKey === key;
  const baseTitle = row
    ? eexMarketTimestampTitle(row)
    : `${cell.instrument.displayName ?? cell.instrument.providerKey} · listed by EEX · no cached top-of-book observation`;
  const values = [
    row?.bidLots ?? null,
    row?.bidPrice ?? null,
    row?.askPrice ?? null,
    row?.askLots ?? null,
    row?.lastTradePrice ?? null,
    row?.lastTradeLots ?? null,
    settlement?.price ?? row?.settlementPrice ?? null,
  ];
  return <>{values.map((value, index) => (
    <td
      key={index}
      title={matrixCellTitle(row, settlement, index, baseTitle)}
      role="button"
      tabIndex={0}
      aria-label={`${cell.instrument.productGroup?.label ?? "Market"} ${cell.instrument.deliveryPeriod?.label ?? "contract"} ${matrixColumnLabel(index)}`}
      onClick={() => onSelect(key)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(key);
        }
      }}
      className={cn(
        "h-7 cursor-pointer whitespace-nowrap border-l border-border/30 px-1 text-right font-semibold tabular-nums hover:ring-1 hover:ring-inset hover:ring-accent",
        sectionStart ? "border-t-2 border-t-border" : "border-t border-t-border/40",
        index <= 1 && "bg-sky-500/[0.06] text-sky-700 dark:text-sky-300",
        (index === 2 || index === 3) && "bg-rose-500/[0.06] text-rose-700 dark:text-rose-300",
        index === 6 && "bg-violet-500/[0.05] text-violet-800 dark:text-violet-200",
        selected && "ring-1 ring-inset ring-accent bg-accent/10"
      )}
    >
      <div>{eexCompactDecimal(value, index === 0 || index === 3 || index === 5 ? 2 : 3)}</div>
      {index === 4 && value != null && (
        <div className="font-sans text-[7px] font-normal leading-none text-muted">
          trade {lastTradeAgeLabel(row!)}
        </div>
      )}
    </td>
  ))}</>;
}

function matrixCellTitle(
  row: TopOfBook | null,
  settlement: SettlementMark | null,
  columnIndex: number,
  baseTitle: string
): string {
  if (!row && columnIndex !== 6) return baseTitle;
  if (!row) return settlementTitle(baseTitle, settlement, null);
  if (columnIndex === 0 || columnIndex === 1) {
    return [
      baseTitle,
      `Bid quantity ${row.bidLots ?? "unavailable"} MW`,
    ].filter(Boolean).join(" · ");
  }
  if (columnIndex === 2 || columnIndex === 3) {
    return [
      baseTitle,
      `Ask quantity ${row.askLots ?? "unavailable"} MW`,
    ].filter(Boolean).join(" · ");
  }
  if (columnIndex === 4 || columnIndex === 5) {
    return row.lastTradePrice != null
      ? [
          baseTitle,
          "Last: EEX public trades",
          `last-trade age ${lastTradeAgeLabel(row)} (independent of top-of-book freshness)`,
          row.lastTradeLots != null ? `quantity ${row.lastTradeLots} MW` : null,
          row.lastTradeAt ? `market ${row.lastTradeAt}` : null,
          row.lastTradeReceivedAt ? `received ${row.lastTradeReceivedAt}` : null,
        ].filter(Boolean).join(" · ")
      : `${baseTitle} · Last: unavailable`;
  }
  return settlementTitle(baseTitle, settlement, row);
}

function lastTradeAgeLabel(
  row: Pick<TopOfBook, "lastTradeAt" | "lastTradeReceivedAt">
): string {
  const timestamp = row.lastTradeAt ?? row.lastTradeReceivedAt;
  return timestamp ? eexObservationAgeLabel(timestamp) : "unknown";
}

function settlementTitle(
  baseTitle: string,
  settlement: SettlementMark | null,
  row: TopOfBook | null
): string {
  const sourceValue = settlement?.source ?? row?.settlementSource ?? null;
  const source = settlementSourceLabel(sourceValue);
  return [
    baseTitle,
    source ? `Settlement: ${source}` : "Settlement: unavailable",
    (settlement?.tradingDate ?? row?.settlementTradingDate)
      ? `trading date ${settlement?.tradingDate ?? row?.settlementTradingDate}`
      : null,
    (settlement?.receivedAt ?? row?.settlementReceivedAt)
      ? `received ${settlement?.receivedAt ?? row?.settlementReceivedAt}`
      : null,
    [settlement?.currency ?? row?.settlementCurrency, settlement?.uom ?? row?.settlementUom]
      .filter(Boolean).join("/") || null,
  ].filter(Boolean).join(" · ");
}

function matrixColumnLabel(index: number): string {
  return ["bid MW", "bid price", "ask price", "ask MW", "last trade price", "last trade MW", "settlement price"][index]
    ?? "market value";
}

function sanitizeMatrixConfiguration(
  input: unknown,
  matrix: MatrixModel,
  fallback: EexMatrixBoardConfiguration
): EexMatrixBoardConfiguration {
  if (!input || typeof input !== "object") return fallback;
  const candidate = input as Partial<EexMatrixBoardConfiguration>;
  if (!Array.isArray(candidate.productGroupKeys) || !Array.isArray(candidate.deliveryPeriodKeys)) return fallback;
  const periodMode = candidate.periodMode === "follow_universe" || candidate.periodMode === "custom"
    ? candidate.periodMode
    : fallback.periodMode;
  const periodKeys = new Set(matrix.periods.map((period) => period.key));
  // Catalogue absence must not erase a saved selection, including when another
  // board setting is applied while that market is temporarily unavailable.
  const productGroupKeys = [...new Set(candidate.productGroupKeys.filter((key): key is string => typeof key === "string" && key.length > 0))];
  const deliveryPeriodKeys = candidate.deliveryPeriodKeys.filter((key): key is string => typeof key === "string" && periodKeys.has(key));
  return {
    productGroupKeys,
    periodMode,
    deliveryPeriodKeys: periodMode === "follow_universe" ? [] : deliveryPeriodKeys,
  };
}

function groupPeriods(periods: EexMatrixDeliveryPeriod[]) {
  const grouped = new Map<EexMatrixDeliveryPeriod["kind"], EexMatrixDeliveryPeriod[]>();
  for (const period of periods) grouped.set(period.kind, [...(grouped.get(period.kind) ?? []), period]);
  return [...grouped.entries()];
}

function groupFeedState(quotes: TopOfBook[]): "live" | "warning" | "disconnected" {
  if (quotes.length === 0 || quotes.every((quote) => quote.freshnessState === "disconnected")) return "disconnected";
  if (quotes.every((quote) =>
    quote.freshnessState === "fresh"
    && quote.coverageState !== "omitted"
    && quote.coverageState !== "unknown"
  )) return "live";
  return "warning";
}

function groupFeedLabel(state: ReturnType<typeof groupFeedState>): string {
  return state === "live" ? "fresh" : state === "warning" ? "attention" : "no current quotes";
}

function summarizeValues<T extends string>(values: T[]): Array<{ value: T; count: number }> {
  const counts = new Map<T, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return [...counts].map(([value, count]) => ({ value, count }));
}

function summarizeQuoteStates(quotes: TopOfBook[]): Array<{
  freshnessState: TopOfBook["freshnessState"];
  coverageState: TopOfBook["coverageState"];
  count: number;
}> {
  const summaries = new Map<string, {
    freshnessState: TopOfBook["freshnessState"];
    coverageState: TopOfBook["coverageState"];
    count: number;
  }>();
  for (const quote of quotes) {
    const key = `${quote.freshnessState}:${quote.coverageState}`;
    const existing = summaries.get(key);
    if (existing) existing.count += 1;
    else summaries.set(key, {
      freshnessState: quote.freshnessState,
      coverageState: quote.coverageState,
      count: 1,
    });
  }
  return [...summaries.values()];
}

function latestTimestamp(values: Array<string | null>): string | null {
  return values.filter((value): value is string => Boolean(value)).sort().at(-1) ?? null;
}

function formatUtcTimestamp(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "unavailable";
  return `${date.toISOString().slice(0, 19).replace("T", " ")} UTC`;
}

async function fetchCachedPages<T>(
  schema: ApiResponseSchema<Page<T>>,
  endpoint: string,
  filter: EexLiveMarketFilter,
  maximumItems = 2_000,
  extra: Record<string, string | number | null | undefined> = {},
  signal?: AbortSignal
): Promise<CachedCollection<T>> {
  const items: T[] = [];
  let offset = 0;
  let asOf = new Date().toISOString();
  let nextOffset: number | null = 0;
  while (nextOffset !== null && items.length < maximumItems) {
    const page = await apiContract(
      schema,
      eexCachedPath(endpoint, filter, {
        ...extra,
        limit: Math.min(2_000, maximumItems - items.length),
        offset,
      }),
      { signal }
    );
    asOf = page.asOf;
    items.push(...page.items);
    nextOffset = page.page.nextOffset;
    if (nextOffset !== null && nextOffset <= offset) break;
    if (nextOffset !== null) offset = nextOffset;
  }
  return { asOf, items: items.slice(0, maximumItems), truncated: nextOffset !== null };
}

function WatchlistFilterButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "h-7 rounded border px-2 text-[10px]",
        active ? "border-accent bg-accent/10 text-accent" : "border-border text-muted hover:text-fg"
      )}
    >
      {label}
    </button>
  );
}

function CompactPriceCell({
  value,
  tone,
  className,
}: {
  value: string | null;
  tone?: "bid" | "ask";
  className?: string;
}) {
  return (
    <td className={cn(
      "whitespace-nowrap border-t border-border/60 px-2 py-1 text-right font-semibold tabular-nums",
      tone === "bid" && "bg-sky-500/[0.06] text-sky-700 dark:text-sky-300",
      tone === "ask" && "bg-rose-500/[0.06] text-rose-700 dark:text-rose-300",
      className
    )}>
      {value ?? "—"}
    </td>
  );
}

function CompactSizeCell({
  lots,
  tone,
}: {
  lots: string | null;
  tone?: "bid" | "ask";
}) {
  return (
    <td className={cn(
      "whitespace-nowrap border-t border-border/60 px-2 py-1 text-right tabular-nums",
      tone === "bid" && "bg-sky-500/[0.06] text-sky-700 dark:text-sky-300",
      tone === "ask" && "bg-rose-500/[0.06] text-rose-700 dark:text-rose-300"
    )}>
      <div>{lots ?? "—"}</div>
    </td>
  );
}

function SettlementCell({ row, settlement }: { row: TopOfBook; settlement: SettlementMark | null }) {
  const price = settlement?.price ?? row.settlementPrice ?? null;
  const tradingDate = settlement?.tradingDate ?? row.settlementTradingDate ?? null;
  const currency = settlement?.currency ?? row.settlementCurrency ?? null;
  const uom = settlement?.uom ?? row.settlementUom ?? null;
  const receivedAt = settlement?.receivedAt ?? row.settlementReceivedAt ?? null;
  const source = settlementSourceLabel(settlement?.source ?? row.settlementSource);
  const unit = [currency, uom].filter(Boolean).join(" / ");
  const title = [
    source,
    tradingDate ? `trading date ${tradingDate}` : null,
    unit || null,
    receivedAt ? `received ${receivedAt}` : null,
  ].filter(Boolean).join(" · ");
  return (
    <td
      className="whitespace-nowrap border-t border-border/60 bg-violet-500/[0.05] px-2 py-1 text-right tabular-nums text-violet-800 dark:text-violet-200"
      title={title || "No current settlement price is cached for this instrument"}
    >
      <div className="font-semibold">{price ?? "—"}</div>
      <div className="text-[9px] opacity-70">
        {tradingDate ?? "not published"}{source ? ` · ${source}` : ""}{unit ? ` · ${unit}` : ""}
      </div>
    </td>
  );
}

function settlementSourceLabel(
  source: TopOfBook["settlementSource"] | SettlementMark["source"]
): string | null {
  if (source === "eex_datasource") return "EEX DataSource";
  if (source === "ecc_span") return "ECC SPAN fallback";
  return null;
}

type TradeTapeSubject = Pick<
  TopOfBook,
  "scopeId" | "subjectId" | "apiFamily" | "commodityCode" | "areaCode" | "providerKey" | "displayName"
> & { lastTradeAt: string | null };

function InstrumentTradeTape({ selected }: { selected: TradeTapeSubject | null }) {
  const query = useQuery({
    queryKey: ["eex-market-data", "instrument-public-tape", selected?.scopeId, selected?.subjectId],
    queryFn: () => apiContract(
      EexMarketDataPublicTradesResponseSchema,
      eexCachedPath("trades", {
        apiFamily: selected!.apiFamily,
        marketSegment: "",
        commodityCode: selected!.commodityCode,
        areaCode: selected!.areaCode,
        dataset: "public_trades",
      }, {
        subjectId: selected!.subjectId,
        mode: "current",
        limit: 25,
        offset: 0,
      })
    ),
    enabled: selected !== null,
    retry: false,
    refetchInterval: 5_000,
  });
  const name = selected?.displayName ?? selected?.providerKey;
  return (
    <section className="rounded border border-border/70">
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border/70 px-3 py-2">
        <div>
          <div className="text-xs font-semibold">Short public trade tape</div>
          <div className="text-[10px] text-muted">
            {selected ? name : "Select an instrument above"} · public EEX market activity, not this company&apos;s executions
          </div>
        </div>
        {selected?.lastTradeAt && <div className="text-[10px] text-muted">Latest {formatTime(selected.lastTradeAt)}</div>}
      </div>
      {!selected ? (
        <div className="px-3 py-6 text-center text-xs text-muted">Select a row to inspect its recent public trades.</div>
      ) : query.isLoading ? (
        <div className="px-3 py-6 text-center text-xs text-muted">Loading recent public trades…</div>
      ) : query.isError ? (
        <div className="px-3 py-6 text-center text-xs text-muted">
          The short public tape is not available for this instrument yet. Its bid/ask board remains usable.
        </div>
      ) : !query.data || query.data.items.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-muted">No recent public trades are cached for this instrument.</div>
      ) : (
        <div className="max-h-56 overflow-auto">
          <table className="w-full min-w-[500px] border-collapse font-mono text-[11px]">
            <thead className="sticky top-0 bg-bg text-[10px] uppercase tracking-wide text-muted">
              <tr>
                <th className="border-b border-border px-3 py-1.5 text-left font-medium">Time</th>
                <th className="border-b border-border px-3 py-1.5 text-right font-medium">Price</th>
                <th className="border-b border-border px-3 py-1.5 text-right font-medium">MW</th>
                <th className="border-b border-border px-3 py-1.5 text-left font-medium">Type</th>
                <th className="border-b border-border px-3 py-1.5 text-left font-medium">Update</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((trade) => (
                <tr key={trade.eventKey} className="border-b border-border/50 last:border-0">
                  <td className="whitespace-nowrap px-3 py-1.5">{formatTime(trade.marketTimestamp ?? trade.receivedAt)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{trade.price ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{trade.lots ?? "—"}</td>
                  <td className="px-3 py-1.5 font-sans">{trade.tradeType ?? "—"}</td>
                  <td className="px-3 py-1.5 font-sans text-muted">{trade.rawUpdateAction ?? "observed"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function marketRowKey(row: Pick<TopOfBook, "scopeId" | "subjectId">): string {
  return `${row.scopeId}:${row.subjectId}`;
}

function PublicTapeTable({
  filter,
  historyAvailable,
}: {
  filter: EexLiveMarketFilter;
  historyAvailable: boolean;
}) {
  const [mode, setMode] = useState<"current" | "events">("current");
  const [offset, setOffset] = useState(0);
  useResetPageOnFilterChange(filter, setOffset);
  useEffect(() => {
    if (!historyAvailable && mode === "events") setMode("current");
  }, [historyAvailable, mode]);
  const query = useQuery({
    queryKey: ["eex-market-data", "public-tape", filter, mode, offset],
    queryFn: () => {
      const window = mode === "events" ? eexPublicEventWindow(new Date()) : null;
      return apiContract(
        EexMarketDataPublicTradesResponseSchema,
        eexCachedPath("trades", filter, mode === "events"
          ? { mode, from: window!.from, to: window!.to, companyUniverseSelected: "true", limit: 200, offset }
          : { mode, companyUniverseSelected: "true", limit: 200, offset })
      );
    },
    retry: false,
    refetchInterval: 10_000,
  });
  useResetEmptyPage(query.data, offset, setOffset);
  return <div className="space-y-2">
    <div className="flex items-center gap-1 text-[11px]">
      <button
        type="button"
        onClick={() => setMode("current")}
        className={cn("rounded border px-2 py-1", mode === "current" ? "border-accent text-accent" : "border-border text-muted")}
      >
        Current public tape
      </button>
      <button
        type="button"
        disabled={!historyAvailable}
        title={historyAvailable ? "Show append-only public trade events from the last 24 hours" : "Historical events become available only after this scope advances to the history phase"}
        onClick={() => setMode("events")}
        className={cn("rounded border px-2 py-1 disabled:opacity-35", mode === "events" ? "border-accent text-accent" : "border-border text-muted")}
      >
        24h event history
      </button>
    </div>
    <QueryTable query={query} empty={mode === "events"
      ? "No public EEX trade events were cached in the last 24 hours for these filters."
      : "No current public EEX trades match these filters."}>
    {(data) => (
      <>
        <MarketTable headings={["Time", "Instrument", "Market", "Price", "MW", "Type", "Action"]}>
          {data.items.map((row) => (
            <tr key={row.eventKey} className="border-t border-border/60">
              <td className="whitespace-nowrap px-2 py-1.5 text-xs">{formatTime(row.marketTimestamp ?? row.receivedAt)}</td>
              <Cell primary={row.displayName ?? row.providerKey} secondary={row.providerKey} />
              <Cell primary={row.areaCode} secondary={`${row.commodityCode} · ${row.apiFamily}`} />
              <NumberCell value={row.price} />
              <NumberCell value={row.lots} />
              <td className="px-2 py-1.5 text-xs">{row.tradeType ?? "—"}</td>
              <td className="px-2 py-1.5 text-xs text-muted">{row.rawUpdateAction ?? "observed"}</td>
            </tr>
          ))}
        </MarketTable>
        <PageControls page={data.page} itemCount={data.items.length} onOffsetChange={setOffset} />
      </>
    )}
    </QueryTable>
  </div>;
}

function IndexTable({ filter }: { filter: EexLiveMarketFilter }) {
  const indexFilter = { ...filter, apiFamily: "index" };
  const [offset, setOffset] = useState(0);
  useResetPageOnFilterChange(indexFilter, setOffset);
  const query = useQuery({
    queryKey: ["eex-market-data", "indices", indexFilter, offset],
    queryFn: () => api<Page<IndexPublication>>(eexCachedPath("index-publications", indexFilter, { mode: "current", limit: 200, offset })),
    retry: false,
    refetchInterval: 60_000,
  });
  useResetEmptyPage(query.data, offset, setOffset);
  return <QueryTable query={query} empty="No cached index publications match these filters.">
    {(data) => (
      <>
        <MarketTable headings={["Index", "Hub", "Delivery day", "Price", "Volume", "Unit", "Published age"]}>
          {data.items.map((row) => (
            <tr key={row.revisionKey} className="border-t border-border/60">
              <Cell primary={row.displayName ?? row.indexCode} secondary={row.indexCode} />
              <Cell primary={row.areaCode} secondary={row.commodityCode} />
              <td className="px-2 py-1.5 text-xs">{row.deliveryDay}</td>
              <NumberCell value={row.price} />
              <NumberCell value={row.volume} />
              <td className="px-2 py-1.5 text-xs">{[row.currency, row.uom].filter(Boolean).join(" / ") || "—"}</td>
              <td className="px-2 py-1.5 text-right text-xs text-muted">{eexObservationAgeLabel(row.receivedAt)}</td>
            </tr>
          ))}
        </MarketTable>
        <PageControls page={data.page} itemCount={data.items.length} onOffsetChange={setOffset} />
      </>
    )}
  </QueryTable>;
}

function HealthTable({ query }: { query: ReturnType<typeof useQuery<HealthResponse, Error>> }) {
  return <QueryTable query={query} empty="No cached feed-health rows are available.">
    {(data) => (
      <MarketTable headings={["Market", "Dataset", "Selector", "State", "Rows", "Schema drift", "Rate limits", "Last success"]}>
        {data.items.map((row) => (
          <tr key={row.scopeId} className="border-t border-border/60">
            <Cell primary={row.areaCode} secondary={`${row.commodityCode} · ${row.apiFamily}`} />
            <td className="px-2 py-1.5 text-xs">{row.dataset.replaceAll("_", " ")}</td>
            <td className="px-2 py-1.5 text-xs text-muted">{row.selectorCode ?? "all"}</td>
            <td className="px-2 py-1.5"><StateBadge value={row.displayState} /></td>
            <td className="px-2 py-1.5 text-right font-mono text-xs">{row.returnedRowCount ?? "—"}{row.expectedRowCount != null ? ` / ${row.expectedRowCount}` : ""}</td>
            <td className="px-2 py-1.5 text-right font-mono text-xs">{row.schemaDriftCount}</td>
            <td className="px-2 py-1.5 text-right font-mono text-xs">{row.rateLimitCount}</td>
            <td className="px-2 py-1.5 text-right text-xs text-muted">{row.lastSuccessfulAt ? eexObservationAgeLabel(row.lastSuccessfulAt) : "never"}</td>
          </tr>
        ))}
      </MarketTable>
    )}
  </QueryTable>;
}

function FeedWarnings({ items }: { items: FeedHealth[] }) {
  const warnings = useMemo(() => [...new Set(items.flatMap((item) => item.warnings))], [items]);
  if (warnings.length === 0) return null;
  return (
    <div className="rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-xs text-amber-700 dark:text-amber-300">
      Feed warning: {warnings.map((warning) => warning.replaceAll("_", " ")).join(" · ")}. Cached values remain visible with their age and state.
    </div>
  );
}

function QueryTable<T>({ query, empty, children }: {
  query: { isLoading: boolean; isError: boolean; error: Error | null; data: T | undefined };
  empty: string;
  children: (data: T) => React.ReactNode;
}) {
  if (query.isLoading) return <div className="py-6 text-center text-xs text-muted">Loading cached data…</div>;
  if (query.isError) return <div className="py-6 text-center text-xs text-negRed">{query.error?.message ?? "Cached data request failed"}</div>;
  if (!query.data) return <div className="py-6 text-center text-xs text-muted">{empty}</div>;
  const items = (query.data as { items?: unknown[] }).items;
  if (Array.isArray(items) && items.length === 0) return <div className="py-6 text-center text-xs text-muted">{empty}</div>;
  return <>{children(query.data)}</>;
}

function MarketTable({ headings, children }: { headings: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded border border-border/70">
      <table className="min-w-full border-collapse">
        <thead className="bg-border/20 text-left text-[10px] uppercase tracking-wide text-muted">
          <tr>{headings.map((heading) => <th key={heading} className="whitespace-nowrap px-2 py-1.5 font-medium">{heading}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function PageControls({ page, itemCount, onOffsetChange }: {
  page: Page<unknown>["page"];
  itemCount: number;
  onOffsetChange: (offset: number) => void;
}) {
  if (page.offset === 0 && page.nextOffset === null) return null;
  const first = page.offset + 1;
  const last = page.offset + itemCount;
  return (
    <div className="mt-2 flex items-center justify-end gap-2 text-xs text-muted">
      <span>{first}–{last}</span>
      <button
        type="button"
        disabled={page.offset === 0}
        onClick={() => onOffsetChange(Math.max(0, page.offset - page.limit))}
        className="rounded border border-border px-2 py-1 disabled:opacity-40"
      >
        Previous
      </button>
      <button
        type="button"
        disabled={page.nextOffset === null}
        onClick={() => page.nextOffset !== null && onOffsetChange(page.nextOffset)}
        className="rounded border border-border px-2 py-1 disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}

function useResetPageOnFilterChange(
  filter: EexLiveMarketFilter,
  setOffset: (offset: number) => void
) {
  useEffect(
    () => setOffset(0),
    [
      filter.apiFamily,
      filter.marketSegment,
      filter.commodityCode,
      filter.areaCode,
      filter.dataset,
      setOffset,
    ]
  );
}

function useResetEmptyPage<T>(
  data: Page<T> | undefined,
  offset: number,
  setOffset: (offset: number) => void
) {
  useEffect(() => {
    if (offset > 0 && data?.items.length === 0) setOffset(0);
  }, [data, offset, setOffset]);
}

function Cell({ primary, secondary }: { primary: string; secondary?: string | null }) {
  return <td className="max-w-64 px-2 py-1.5 text-xs"><div className="truncate">{primary}</div>{secondary && secondary !== primary && <div className="truncate text-[10px] text-muted">{secondary}</div>}</td>;
}

function NumberCell({ value }: { value: string | null }) {
  return <td className="px-2 py-1.5 text-right font-mono text-xs tabular-nums">{value ?? "—"}</td>;
}

function FilterSelect({
  label,
  value,
  values,
  onChange,
  mobile = false,
}: {
  label: string;
  value: string;
  values: string[];
  onChange: (value: string) => void;
  mobile?: boolean;
}) {
  return (
    <label className={cn("min-w-0 text-xs text-muted", mobile ? "flex flex-col gap-1" : "flex items-center gap-1")}>
      <span>{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          "min-w-0 rounded border border-border bg-bg px-2 text-fg",
          mobile ? "min-h-11 w-full touch-manipulation text-base" : "h-7 bg-transparent text-xs"
        )}
      >
        <option value="">All</option>
        {values.map((option) => <option key={option} value={option}>{plainFilterOption(option)}</option>)}
      </select>
    </label>
  );
}

function Tab({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return <button type="button" onClick={onClick} className={cn("inline-flex items-center gap-1 border-b-2 px-2 py-1.5 text-xs", active ? "border-accent text-accent" : "border-transparent text-muted hover:text-fg")}>{icon}{label}</button>;
}

function StateBadge({
  value,
  detail,
  showDetail = false,
  label,
}: {
  value: string;
  detail?: string;
  showDetail?: boolean;
  label?: string;
}) {
  const live = value === "fresh" || value === "live";
  const disconnected = value === "disconnected";
  const visibleDetail = detail && (showDetail || detail !== "quoted") ? detail : null;
  return <span className={cn(
    "inline-flex rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide",
    live
      ? "bg-posGreen/10 text-posGreen"
      : disconnected
        ? "bg-negRed/10 text-negRed"
        : "bg-warnOrange/10 text-warnOrange sm:bg-amber-500/10 sm:text-amber-700 sm:dark:text-amber-300"
  )}>{label ?? value}{visibleDetail ? ` · ${visibleDetail}` : ""}</span>;
}

function HealthBadge({ state }: { state: "live" | "warning" | "disconnected" }) {
  const label = state === "live" ? "Updating normally" : state === "warning" ? "Update problem" : "Updates stopped";
  return <span className={cn("rounded border px-2 py-0.5 text-xs", state === "live" ? "border-posGreen/40 text-posGreen" : state === "disconnected" ? "border-negRed/40 text-negRed" : "border-amber-500/40 text-amber-700 dark:text-amber-300")}>{label}</span>;
}

function plainFeedState(value: string): string {
  if (value === "live") return "Updating normally";
  if (value === "stale") return "Updates delayed";
  if (value === "partial") return "Some prices missing";
  if (value === "degraded") return "Update problem";
  if (value === "disconnected") return "Updates stopped";
  return "Status unavailable";
}

function plainQuoteState(
  freshness: TopOfBook["freshnessState"],
  coverage: TopOfBook["coverageState"]
): string {
  if (coverage === "omitted") return "No recent price";
  if (coverage === "unknown") return "Not checked yet";
  if (coverage === "unquoted") return freshness === "fresh" ? "No bid or ask now" : "Last check had no bid or ask";
  if (freshness === "fresh") return "Current bid / ask";
  if (freshness === "stale") return "Saved price · updates delayed";
  return "Last saved price · updates stopped";
}

function plainFilterOption(value: string): string {
  const labels: Record<string, string> = {
    derivatives: "Futures / derivatives",
    gas_spot: "Spot gas",
    index: "Published prices",
    POWER: "Power",
    NATURAL_GAS: "Natural gas",
    top_of_book: "Best bid / ask",
    public_trades: "Recent public trades",
    index_publications: "Published prices",
  };
  return labels[value] ?? value.replaceAll("_", " ");
}

function formatTime(value: string): string {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toLocaleString() : "—";
}

function matchesScopeFilter(
  scope: ScopeResponse["scopes"][number],
  filter: EexLiveMarketFilter
): boolean {
  return (!filter.apiFamily || scope.apiFamily === filter.apiFamily)
    && (!filter.marketSegment || scope.marketSegment === filter.marketSegment)
    && (!filter.commodityCode || scope.commodityCode === filter.commodityCode)
    && (!filter.areaCode || scope.areaCode === filter.areaCode)
    && (!filter.dataset || scope.dataset === filter.dataset);
}
